import { CustomersAPI } from "api/customers";
import { DocumentServicesAPI } from "api/documentServices";
import { FilesAPI } from "api/files";
import { MessagesAPI } from "api/messages";
import { SkickaDocumentsAPI } from "api/skickaDocuments";
import { getFavoriteUserCustomer } from "common/customer/getFavoriteCustomer";
import {
  clearCompanyName,
  isInternalUser,
  setCompanyName,
  setWorkingTabCustomer
} from "common/MetaActions";
import { ignoreStaleResponse } from "helpers/ignoreStaleResponse";
import {
  AllCustomers,
  CustomerState,
  MyCustomer,
  MyCustomers
} from "models/customers/customers";
import { Counts, CustomerDocuments } from "models/customers/documents";
import { RootState } from "store";
import { StateCreator } from "zustand";

export interface CustomersSlice {
  allCustomers: AllCustomers;
  myCustomers: MyCustomers;
  workingCustomer: CustomerState;

  setLoadingMyCustomers: (isLoading: boolean) => void;
  setLoadingAllCustomers: (isLoading: boolean) => void;
  setLoadingCustomer: (isLoading: boolean) => void;
  setAllCustomers: (customers: MyCustomer[]) => void;
  setMyCustomers: (customers: MyCustomer[]) => void;
  getDefaultCustomer: () => CustomerState;
  resetWorkingCustomer: () => void;
  setCustomer: (customer: CustomerState) => void;
  fetchCustomers: (shouldSetWorkingCustomerId?: boolean) => void;
  closeCustomer: (customerId: string) => void;
  searchForCustomers: (searchTerm: string) => void;
  fetchCustomer: (id: string) => void;
  setCustomerDocuments: (data: CustomerDocuments) => void;
  resetCustomerDocuments: () => void;
  setCustomerDocumentsLoading: (isLoading: boolean) => void;
  setCustomerDocumentsCount: (data: Counts) => void;
  setCustomerDocumentsPageNumber: (page: number) => void;
  setCustomerDocumentsPageSize: (size: number) => void;
  fetchDocumentsCount: (customerId: string) => Promise<void>;
  fetchCustomerDocuments: (customerId: string, params: any) => Promise<void>;
}

export const createCustomersSlice: StateCreator<
  RootState,
  [["zustand/devtools", never]],
  [],
  CustomersSlice
> = (set, get) => ({
  allCustomers: {
    isLoading: false,
    imported: {},
    customers: []
  },
  myCustomers: {
    isLoading: false,
    customers: [],
    unfilteredCustomers: []
  },
  workingCustomer: {
    id: "",
    customerId: "",
    isLoading: false,
    closingAt: new Date(),
    customerNumber: "",
    state: "",
    contactInformation: {
      address1: "",
      zipCode: "",
      city: "",
      phone: ""
    },
    fpService: {},
    activeProjectTypes: [],
    externalUsers: [],
    internalUsers: [],
    customerName: "",
    documentServices: [],
    documents: {
      isLoading: false,
      currentPage: 1,
      pageSize: 15
    }
  },

  setLoadingMyCustomers: (isLoading) =>
    set(
      ({ customers }) => ({
        customers: {
          ...customers,
          myCustomers: {
            ...customers.myCustomers,
            isLoading
          }
        }
      }),
      false,
      "customers/SET_LOADING_MY_CUSTOMERS"
    ),

  setLoadingAllCustomers: (isLoading) =>
    set(
      ({ customers }) => ({
        customers: {
          ...customers,
          allCustomers: {
            ...customers.allCustomers,
            isLoading
          }
        }
      }),
      false,
      "customers/SET_LOADING_ALL_CUSTOMERS"
    ),

  setLoadingCustomer: (isLoading) =>
    set(
      ({ customers }) => ({
        customers: {
          ...customers,
          workingCustomer: {
            ...customers.workingCustomer,
            isLoading
          }
        }
      }),
      false,
      "customers/SET_LOADING_CUSTOMER"
    ),

  setAllCustomers: (allCustomers) =>
    set(
      ({ customers }) => ({
        customers: {
          ...customers,
          allCustomers: {
            ...customers.allCustomers,
            customers: allCustomers
          }
        }
      }),
      false,
      "customers/SET_ALL_CUSTOMERS"
    ),

  setMyCustomers: (myCustomers) => {
    const filteredCustomers = myCustomers.filter(
      (customer) =>
        customer.state !== "New" &&
        customer.state !== "Closed" &&
        customer.state !== "ReactivatedInMaconomy"
    );

    set(
      ({ customers }) => ({
        customers: {
          ...customers,
          myCustomers: {
            ...customers.myCustomers,
            customers: filteredCustomers,
            unfilteredCustomers: myCustomers
          }
        }
      }),
      false,
      "customers/SET_MY_CUSTOMERS"
    );
  },

  getDefaultCustomer: (): CustomerState => ({
    id: "",
    customerId: "",
    isLoading: false,
    closingAt: new Date(),
    customerNumber: "",
    state: "",
    contactInformation: { address1: "", zipCode: "", city: "", phone: "" },
    fpService: {},
    activeProjectTypes: [],
    externalUsers: [],
    internalUsers: [],
    documentServices: [],
    customerName: "",
    documents: { isLoading: false, currentPage: 1, pageSize: 15 }
  }),

  resetWorkingCustomer: () => {
    set(
      ({ customers }) => ({
        customers: {
          ...customers,
          workingCustomer: get().customers.getDefaultCustomer()
        }
      }),
      false,
      "customers/RESET_WORKING_CUSTOMER"
    );
  },

  setCustomer: (customer) =>
    set(
      ({ customers }) => ({
        customers: {
          ...customers,
          workingCustomer: customer
        }
      }),
      false,
      "customers/SET_CUSTOMER"
    ),

  fetchCustomers: async (shouldSetWorkingCustomerId = false) => {
    if (get().customers.myCustomers.isLoading) {
      return;
    }
    const { appendErrorNotification } = get().error;

    const getCustomersInternal = async (): Promise<MyCustomer[]> => {
      set({}, false, "customers/FETCH_MY_CUSTOMERS");
      get().customers.setLoadingMyCustomers(true);
      try {
        const result = await CustomersAPI.getCustomersInternal();
        return result;
      } catch (error) {
        appendErrorNotification(
          "Vi kunde tyvärr inte hämta dina kunder just nu.",
          error
        );
        return [];
      } finally {
        get().customers.setLoadingMyCustomers(false);
      }
    };

    const getFilesSummary = async () => {
      if (!isInternalUser()) {
        return {};
      }
      const filesSummary = await FilesAPI.getFilesSummary();
      return filesSummary;
    };

    const [customers, filesSummary, messagesSummary] = await Promise.all([
      getCustomersInternal(),
      getFilesSummary(),
      MessagesAPI.getMessagesSummary()
    ]);

    // Set unhandled/unread counts. Default counts to 0
    const customersWithSummary: MyCustomer[] = customers.map((customer) => ({
      ...customer,
      filesSummary:
        filesSummary &&
        Object.keys(filesSummary).indexOf(customer.customerId) > -1 &&
        filesSummary[customer.customerId],
      messagesSummary:
        messagesSummary &&
        Object.keys(messagesSummary).indexOf(customer.customerId) > -1 &&
        messagesSummary[customer.customerId]
    }));
    get().customers.setMyCustomers(customersWithSummary);

    if (shouldSetWorkingCustomerId && customers.length > 0) {
      const userFavoriteCustomer = getFavoriteUserCustomer(
        get().user.userInfo?.id ?? ""
      );
      const {
        setUserFavoriteCustomer,
        setWorkingCustomerId,
        workingCustomerId
      } = get().global;
      setUserFavoriteCustomer(userFavoriteCustomer);
      const initialCustomer = userFavoriteCustomer
        ? userFavoriteCustomer.customerId
        : customers[0].customerId;

      const customerId = workingCustomerId
        ? workingCustomerId
        : initialCustomer;
      setWorkingCustomerId(customerId);
    }
  },

  closeCustomer: async (customerId) => {
    try {
      set({}, false, "customers/CLOSE_CUSTOMER");
      await CustomersAPI.closeCustomer(customerId);
      const { fetchNotifications } = get().notifications;
      fetchNotifications();
      get().customers.fetchCustomers();
    } catch (error) {
      const { appendErrorNotification } = get().error;
      appendErrorNotification("Det gick inte att stänga kunden.", error);
    }
  },

  searchForCustomers: async (searchTerm) => {
    set({}, false, "customers/SEARCH_FOR_CUSTOMERS");
    if (get().customers.allCustomers.isLoading) {
      return;
    }
    get().customers.setLoadingAllCustomers(true);
    try {
      const customers = await CustomersAPI.searchForCustomers(searchTerm);
      get().customers.setAllCustomers(customers);
    } catch (error) {
      const { appendErrorNotification } = get().error;
      appendErrorNotification("Hämtning av kunddata misslyckades.", error);
    } finally {
      get().customers.setLoadingAllCustomers(false);
    }
  },

  fetchCustomer: async (id) => {
    const requestId = id;
    set({}, false, "customers/FETCH_CUSTOMER");

    if (!id) {
      get().error.appendErrorNotification(
        "Kundnummer saknas eller är odefinierat.",
        {}
      );
      clearCompanyName();
      return;
    }

    get().customers.setLoadingCustomer(true);

    try {
      const [customer, documentServices] = await Promise.all([
        CustomersAPI.getCustomerSettings(id),
        DocumentServicesAPI.getDocumentServices(id)
      ]);

      const activeId = get().global.workingCustomerId;
      if (ignoreStaleResponse(activeId, requestId, "fetchCustomer")) {
        return;
      }

      const loadedCustomer = {
        ...customer,
        documentServices,
        isLoading: false,
        acceptedTerms: customer.acceptedTerms,
        documents: customer.documents ?? {
          isLoading: false,
          currentPage: 1,
          pageSize: 15
        }
      };

      setWorkingTabCustomer(requestId);
      setCompanyName(loadedCustomer.customerName);

      get().customers.setCustomer(loadedCustomer);
    } catch (error) {
      const currentId = get().global?.workingCustomerId;
      if (currentId === requestId) {
        get().error.appendErrorNotification(
          "Ett fel uppstod vid hämtning av kunden.",
          error
        );
      }
    } finally {
      const currentId = get().global?.workingCustomerId;

      if (currentId === requestId) {
        get().customers.setLoadingCustomer(false);
      }
    }
  },

  setCustomerDocuments: (data) =>
    set(
      ({ customers }) => ({
        customers: {
          ...customers,
          workingCustomer: {
            ...customers.workingCustomer,
            documents: {
              ...customers.workingCustomer.documents,
              data
            }
          }
        }
      }),
      false,
      "customers/SET_CUSTOMER_DOCUMENTS"
    ),

  resetCustomerDocuments: () => {
    set(
      ({ customers }) => ({
        customers: {
          ...customers,
          workingCustomer: {
            ...customers.workingCustomer,
            documents: {
              ...customers.workingCustomer.documents,
              data: undefined
            }
          }
        }
      }),
      false,
      "customers/RESET_CUSTOMER_DOCUMENTS"
    );
  },

  setCustomerDocumentsLoading: (isLoading) => {
    set(
      ({ customers }) => ({
        customers: {
          ...customers,
          workingCustomer: {
            ...customers.workingCustomer,
            documents: {
              ...customers.workingCustomer.documents,
              isLoading
            }
          }
        }
      }),
      false,
      "customers/SET_CUSTOMER_DOCUMENTS_LOADING"
    );
  },

  setCustomerDocumentsCount: (data) => {
    set(
      ({ customers }) => ({
        customers: {
          ...customers,
          workingCustomer: {
            ...customers.workingCustomer,
            documents: {
              ...customers.workingCustomer.documents,
              total: data.total,
              emails: data.count_per_sender,
              categories: data.count_per_category
            }
          }
        }
      }),
      false,
      "customers/SET_CUSTOMER_DOCUMENTS_COUNT"
    );
  },

  setCustomerDocumentsPageNumber: (page) => {
    set(
      ({ customers }) => ({
        customers: {
          ...customers,
          workingCustomer: {
            ...customers.workingCustomer,
            documents: {
              ...customers.workingCustomer.documents,
              currentPage: page
            }
          }
        }
      }),
      false,
      "setCustomerDocumentsPageNumber"
    );
  },

  setCustomerDocumentsPageSize: (size) => {
    set(
      ({ customers }) => ({
        customers: {
          ...customers,
          workingCustomer: {
            ...customers.workingCustomer,
            documents: {
              ...customers.workingCustomer.documents,
              pageSize: size
            }
          }
        }
      }),
      false,
      "setCustomerDocumentsPageSize"
    );
  },

  fetchDocumentsCount: async (customerId) => {
    try {
      get().customers.setCustomerDocumentsLoading(true);
      const result = await SkickaDocumentsAPI.fetchDocumentsCount(customerId);
      get().customers.setCustomerDocumentsCount(result);
      get().customers.setCustomerDocumentsLoading(false);
    } catch (error) {
      get().error.appendErrorNotification(
        "Hämtning av dokumentantal misslyckades.",
        error
      );
      get().customers.setCustomerDocumentsLoading(false);
    }
    set({}, false, "customers/FETCH_DOCUMENTS_COUNT");
  },

  fetchCustomerDocuments: async (customerId, params) => {
    let currentParams = params;
    let aggregatedDocuments = get().customers.workingCustomer.documents.data;
    set({}, false, "customers/FETCH_CUSTOMER_DOCUMENTS");
    try {
      get().customers.setCustomerDocumentsLoading(true);

      // Fetch documents again if the previous request did not fill the page
      // or if LastEvaluatedKey is still returned
      // when searching or filtering the BE is looking only into the first 10 pages
      // we need additional requests if the first 10 pages did not return the full page size number of items
      // or until we reach the last page - lastEvaluatedKey === null
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const result = await SkickaDocumentsAPI.fetchCustomerDocuments(
          customerId,
          currentParams
        );

        aggregatedDocuments = !aggregatedDocuments
          ? result
          : {
              ...aggregatedDocuments,
              skickaDocuments: [
                ...(aggregatedDocuments.skickaDocuments ?? []),
                ...(result.skickaDocuments ?? [])
              ],
              lastEvaluatedKey: result.lastEvaluatedKey
            };

        if (aggregatedDocuments.lastEvaluatedKey === null) {
          const totalFiltered = aggregatedDocuments.skickaDocuments.length ?? 0;
          set(
            ({ customers }) => ({
              customers: {
                ...customers,
                workingCustomer: {
                  ...customers.workingCustomer,
                  documents: {
                    ...customers.workingCustomer.documents,
                    totalFiltered
                  }
                }
              }
            }),
            false,
            "customers/SET_TOTAL_FILTERED_COUNT"
          );
        }

        get().customers.setCustomerDocuments(aggregatedDocuments);

        const { currentPage, pageSize } =
          get().customers.workingCustomer.documents;
        const documents = aggregatedDocuments.skickaDocuments ?? [];
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const documentsOnCurrentPage = documents.slice(startIndex, endIndex);

        if (
          documentsOnCurrentPage.length >= pageSize ||
          !result.lastEvaluatedKey
        ) {
          get().customers.setCustomerDocumentsLoading(false);
          break;
        }

        currentParams = {
          ...currentParams,
          lastEvaluatedKey: result.lastEvaluatedKey
        };
      }
    } catch (error) {
      get().error.appendErrorNotification(
        "Hämtning av dokumentet misslyckades.",
        error
      );
      get().customers.setCustomerDocumentsLoading(false);
    }
  }
});
